/**
 * @file `useConfig` — TanStack-backed read of the lab daemon's runtime
 *       config (analytics tracker) for the learner surface.
 * @module @labcraft/lab/ui/hooks/use-config
 *
 * The static SPA bundle can't bake the analytics tracker config — the
 * script URL + website ID are operator-set in the admin lab settings
 * panel and arrive at runtime from `GET /api/config`, fetched once on
 * mount and shared across every reader via the `qkConfig` cache entry.
 * The shared `@labcraft/ui` `useAnalytics(config.data?.analytics ??
 * null)` injects the tracker only when both values are present, so the
 * learner surface ships analytics-dark by default.
 *
 * Convention: hooks live in this directory and never call `useQuery`
 * outside it. Components import workspace hooks (`useConfig`, …) only.
 */

import { useQuery } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import type { LabConfigResponse } from '../../shared/routes.ts'
import { qkConfig } from './query_keys.ts'

/**
 * Subscribe to the lab daemon's runtime config from React. The first
 * reader drives the fetch; subsequent readers share the cache. Returns
 * the full TanStack result — callers read `data?.analytics` and supply
 * their own loading-state fallback.
 */
export function useConfig() {
  const client = useClient<Lab>()
  return useQuery<LabConfigResponse>({
    queryKey: qkConfig,
    queryFn: () => client.config(),
  })
}
